/**
 * Expense 模块工具函数
 * 
 * 费用管理相关的通用工具函数
 */

/**
 * 货币符号映射表
 */
export const CURRENCY_SYMBOLS: Record<string, string> = {
  HKD: 'HK$',
  USD: '$',
  CNY: '¥',
  KRW: '₩',
  JPY: '¥',
  TWD: 'NT$',
  EUR: '€',
  GBP: '£',
  SGD: 'S$',
  MOP: 'MOP$',
  AUD: 'A$',
  CAD: 'C$',
  THB: '฿',
  MYR: 'RM',
  VND: '₫',
};

/**
 * 费用类别配置
 */
export const EXPENSE_CATEGORIES = {
  transport: { icon: 'directions-bus', color: '#3498db' },
  accommodation: { icon: 'hotel', color: '#9b59b6' },
  food: { icon: 'restaurant', color: '#e74c3c' },
  flight: { icon: 'flight', color: '#2980b9' },
  taxi: { icon: 'local-taxi', color: '#f39c12' },
  attractions: { icon: 'place', color: '#27ae60' },
  shopping: { icon: 'shopping-bag', color: '#1abc9c' },
  entertainment: { icon: 'local-play', color: '#e67e22' },
  insurance: { icon: 'security', color: '#8e44ad' },
  visa: { icon: 'card-membership', color: '#34495e' },
  communication: { icon: 'phone', color: '#16a085' },
  general: { icon: 'more-horiz', color: '#95a5a6' },
} as const;

export type ExpenseCategoryKey = keyof typeof EXPENSE_CATEGORIES;

/**
 * 格式化大金额为紧凑格式（如 1.2K, 3.5M, 1.2B）
 * 
 * @param amount - 金额数值
 * @param currency - 货币代码（可选）
 * @returns 格式化后的字符串
 */
export function formatCurrencyCompact(amount: number, currency?: string): string {
  if (amount == null || Number.isNaN(Number(amount))) return '';
  
  const n = Number(amount);
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';

  const units = [
    { value: 1e12, suffix: 'T' },
    { value: 1e9, suffix: 'B' },
    { value: 1e6, suffix: 'M' },
    { value: 1e3, suffix: 'K' },
  ];

  for (const u of units) {
    if (abs >= u.value) {
      const v = abs / u.value;
      const formatted = v >= 100 ? Math.round(v).toString() : v.toFixed(1).replace(/\.0$/, '');
      const sym = currency && CURRENCY_SYMBOLS[currency] ? CURRENCY_SYMBOLS[currency] : (currency || '');
      return `${sign}${sym}${formatted}${u.suffix}`;
    }
  }

  // 小金额使用常规格式
  const sym = currency && CURRENCY_SYMBOLS[currency] ? CURRENCY_SYMBOLS[currency] : (currency || '');
  return `${sign}${sym}${abs.toFixed(2)}`;
}

/**
 * 限制数值在安全范围内，防止数值溢出
 * 
 * @param value - 原始数值
 * @param scale - 小数位数（默认2位）
 * @returns 限制后的数值
 */
export function clampToNumeric(value: number | null | undefined, scale = 2): number | null {
  if (value == null) return null;
  
  const n = Number(value);
  if (Number.isNaN(n)) return null;
  
  const factor = Math.pow(10, scale);
  const v = Math.round(n * factor);
  const max = Number.MAX_SAFE_INTEGER;
  const clamped = Math.max(-max, Math.min(max, v));
  
  if (clamped !== v) {
    console.warn('[clampToNumeric] Clamped numeric value', {
      original: n,
      clamped: clamped / factor,
    });
  }
  
  return clamped / factor;
}

/**
 * 解析收据金额（考虑货币转换）
 * 
 * @param receipt - 收据对象
 * @param baseCurrency - 基准货币
 * @returns 解析后的金额
 */
export function resolveReceiptAmount(
  receipt: {
    amount?: number | string;
    converted_amount?: number | string;
    currency?: string;
  } | null | undefined,
  baseCurrency?: string
): number {
  if (!receipt) return 0;

  const receiptCurrency = receipt.currency;
  const converted = receipt.converted_amount;
  const parsedConverted = converted != null ? Number(converted) : NaN;

  // 如果有转换金额且货币不同，使用转换后的金额
  if (
    converted != null &&
    !Number.isNaN(parsedConverted) &&
    receiptCurrency &&
    baseCurrency &&
    receiptCurrency !== baseCurrency
  ) {
    return parsedConverted;
  }

  // 否则使用原始金额
  const parsedAmount = Number(receipt.amount ?? 0);
  return Number.isNaN(parsedAmount) ? 0 : parsedAmount;
}

/**
 * 解析上传者ID（兼容多种字段名）
 * 
 * @param receipt - 收据对象
 * @returns 上传者用户ID
 */
export function resolveUploaderId(receipt: any): string | null {
  if (!receipt) return null;
  return (
    receipt.uploader?.id ||
    receipt.uploader_id ||
    receipt.created_by ||
    receipt.payer_id ||
    null
  );
}

/**
 * 判断收据是否涉及某个用户
 * 
 * @param receipt - 收据对象
 * @param userId - 用户ID
 * @returns 是否涉及该用户
 */
export function receiptInvolvesUser(receipt: any, userId: string | null): boolean {
  if (!receipt || !userId) return false;

  // 检查是否为上传者
  if (resolveUploaderId(receipt) === userId) {
    return true;
  }

  // 检查是否为参与者（私账）
  const members = receipt.members;
  if (Array.isArray(members) && members.length > 0) {
    return members.some((member: any) => member?.user_id === userId);
  }

  return false;
}
